/**
 * BlobTracker - Tracks blobs across frames with persistent IDs
 * 
 * This implements centroid tracking to maintain object identity across frames,
 * preventing duplicate ball creation from motion blur or blob splitting.
 * 
 * Key principles:
 * - Each blob gets a unique ID that persists across frames
 * - Blobs are matched frame-to-frame using Euclidean distance
 * - A blob can only create ONE ball (via ballCreated flag)
 * - Handles temporary disappearance (blob not detected for a few frames)
 */

export interface TrackedBlob {
  id: number;
  x: number;
  y: number;
  lastSeen: number;
  createdAt: number;
  wasInStartZone: boolean;  // Was this blob in START zone in previous frame?
  isInStartZone: boolean;   // Is this blob currently in START zone?
  everInStartZone: boolean; // Has this blob EVER been in START zone? (prevents blobs created outside START from triggering ball creation)
  ballCreated: boolean;     // Has this blob already created a ball?
  framesDisappeared: number; // How many frames since blob was last seen
}

export interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

export class BlobTracker {
  private trackedBlobs: Map<number, TrackedBlob> = new Map();
  private nextBlobId: number = 0;

  // Tunable parameters
  private readonly MAX_TRACKING_DISTANCE = 400; // Max distance to match blob between frames (matches ballDetection setting)
  private readonly MAX_DISAPPEARED_FRAMES = 15; // Remove blob after this many frames of disappearance
  private readonly START_ZONE_PROXIMITY = 50; // Distance from START zone to consider blob as "just exited" (pixels)
  
  /**
   * Update tracked blobs with new detections from current frame
   * @param currentBlobs - Array of blob centroids detected in current frame
   * @param startZone - START zone rectangle (optional)
   * @param timestamp - Current timestamp in milliseconds
   * @returns Array of tracked blobs that exited START zone and haven't created a ball yet
   */
  update(
    currentBlobs: Array<{ x: number; y: number }>,
    startZone: Rectangle | undefined,
    timestamp: number
  ): TrackedBlob[] {
    const matchedBlobIds = new Set<number>();
    const matchedCurrentIndices = new Set<number>();
    const blobsReadyForBallCreation: TrackedBlob[] = [];
    
    // Step 1: Match current blobs to existing tracked blobs
    for (const [blobId, trackedBlob] of this.trackedBlobs.entries()) {
      let bestMatch: { index: number; distance: number } | null = null;

      // Find closest unmatched current blob
      for (let index = 0; index < currentBlobs.length; index++) {
        if (matchedCurrentIndices.has(index)) continue;

        const currentBlob = currentBlobs[index];
        const distance = this.calculateDistance(
          trackedBlob.x,
          trackedBlob.y,
          currentBlob.x,
          currentBlob.y
        );

        if (distance < this.MAX_TRACKING_DISTANCE) {
          if (!bestMatch || distance < bestMatch.distance) {
            bestMatch = { index, distance };
          }
        }
      }

      if (bestMatch) {
        // Update existing tracked blob
        const matchIndex = bestMatch.index;
        const currentBlob = currentBlobs[matchIndex];
        trackedBlob.wasInStartZone = trackedBlob.isInStartZone;
        trackedBlob.isInStartZone = !startZone || this.isInRectangle(currentBlob.x, currentBlob.y, startZone);

        // NOTE: Do NOT update everInStartZone here - it should only be set at blob creation
        // This prevents blobs created outside START zone from triggering ball creation if they later pass through START zone

        trackedBlob.x = currentBlob.x;
        trackedBlob.y = currentBlob.y;
        trackedBlob.lastSeen = timestamp;
        trackedBlob.framesDisappeared = 0;

        matchedBlobIds.add(blobId);
        matchedCurrentIndices.add(matchIndex);

        // Check if this blob exited START zone and is ready for ball creation
        // IMPORTANT: Only create ball if blob was created in START zone initially (everInStartZone flag)
        if (trackedBlob.wasInStartZone && !trackedBlob.isInStartZone && !trackedBlob.ballCreated && trackedBlob.everInStartZone) {
          console.log(`[BLOB ${blobId}] ✅ EXITED START zone - ready for ball creation at (${Math.round(trackedBlob.x)}, ${Math.round(trackedBlob.y)})`);
          blobsReadyForBallCreation.push(trackedBlob);
        } else if (trackedBlob.wasInStartZone && !trackedBlob.isInStartZone) {
          console.log(`[BLOB ${blobId}] ⚠️ EXITED START but NOT creating ball - ballCreated: ${trackedBlob.ballCreated}, everInStart: ${trackedBlob.everInStartZone}`);
        }
      } else {
        // Blob not seen this frame - increment disappeared counter
        trackedBlob.framesDisappeared++;
      }
    }
    
    // Step 2: Create new tracked blobs for unmatched current blobs
    currentBlobs.forEach((currentBlob, index) => {
      if (matchedCurrentIndices.has(index)) return;

      const isInStart = !startZone || this.isInRectangle(currentBlob.x, currentBlob.y, startZone);
      const isNearStart = startZone ? this.isNearStartZoneExit(currentBlob.x, currentBlob.y, startZone) : false;

      // Consider blob as "from START zone" if it's IN START or NEAR START (just exited)
      // This handles fast-moving balls that are detected after they've already left START zone
      const fromStartZone = isInStart || isNearStart;

      const newBlob: TrackedBlob = {
        id: this.nextBlobId++,
        x: currentBlob.x,
        y: currentBlob.y,
        lastSeen: timestamp,
        createdAt: timestamp,
        wasInStartZone: false,
        isInStartZone: isInStart,
        everInStartZone: fromStartZone, // Mark as from START if in or near START zone
        ballCreated: false,
        framesDisappeared: 0,
      };

      if (newBlob.id === 0) {
        console.log(`[BLOB TRACKER] START zone:`, startZone);
      }
      console.log(`[BLOB ${newBlob.id}] 🆕 Created at (${Math.round(newBlob.x)}, ${Math.round(newBlob.y)}) - inStart: ${isInStart}, nearStart: ${isNearStart}, everInStart: ${newBlob.everInStartZone}`);

      this.trackedBlobs.set(newBlob.id, newBlob);

      // If blob is created NEAR START zone (just exited), create ball immediately
      // This handles fast-moving balls that are detected after they've already left START zone
      if (isNearStart && !isInStart && fromStartZone) {
        console.log(`[BLOB ${newBlob.id}] ✅ Created NEAR START zone - ready for ball creation immediately`);
        blobsReadyForBallCreation.push(newBlob);
      }
    });
    
    // Step 3: Remove blobs that have disappeared for too long
    this.cleanup(timestamp);
    
    return blobsReadyForBallCreation;
  }
  
  /**
   * Mark a blob as having created a ball
   */
  markBallCreated(blobId: number): void {
    const blob = this.trackedBlobs.get(blobId);
    if (blob) {
      blob.ballCreated = true;
    }
  }
  
  /**
   * Get all currently tracked blobs
   */
  getTrackedBlobs(): TrackedBlob[] {
    return Array.from(this.trackedBlobs.values());
  }
  
  /**
   * Reset tracker state
   */
  reset(): void {
    this.trackedBlobs.clear();
    this.nextBlobId = 0;
  }

  /**
   * Remove blobs that have disappeared for too long
   */
  private cleanup(timestamp: number): void {
    const blobsToRemove: number[] = [];

    for (const [blobId, blob] of this.trackedBlobs.entries()) {
      if (blob.framesDisappeared > this.MAX_DISAPPEARED_FRAMES) {
        blobsToRemove.push(blobId);
      }
    }

    blobsToRemove.forEach(id => this.trackedBlobs.delete(id));
  }

  /**
   * Calculate Euclidean distance between two points
   */
  private calculateDistance(x1: number, y1: number, x2: number, y2: number): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Check if point is inside rectangle
   */
  private isInRectangle(x: number, y: number, rect: Rectangle): boolean {
    return (
      x >= rect.x &&
      x <= rect.x + rect.width &&
      y >= rect.y &&
      y <= rect.y + rect.height
    );
  }

  /**
   * Check if point is near START zone EXIT (just exited upward)
   * This handles fast-moving balls that are detected after they've already left START zone
   * ONLY considers blobs ABOVE the START zone (y < rect.y) as "just exited"
   */
  private isNearStartZoneExit(x: number, y: number, rect: Rectangle): boolean {
    // Must be ABOVE START zone (y < rect.y) - balls exit upward
    const isAboveStartZone = y < rect.y;

    // Must be within PROXIMITY distance above START zone
    const isNearTop = y >= rect.y - this.START_ZONE_PROXIMITY && y < rect.y;

    // Must be within horizontal bounds of START zone (or close to it)
    const isWithinHorizontalBounds = x >= rect.x - 50 && x <= rect.x + rect.width + 50;

    return isAboveStartZone && isNearTop && isWithinHorizontalBounds;
  }
}

